import {
    Box,
    Card,
    Typography,
    Table,
    TableCell,
    TableHead,
    styled,
    Stack,
    TableBody,
    TableRow,
    useTheme,
    Snackbar,
    Alert,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppPagination from 'components/AppPagination';
import SearchArea from 'page-sections/operator/registration/search-area';
import columnShape from 'page-sections/operator/registration/columnShape';
import {
    useAsyncDebounce,
    useGlobalFilter,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import Scrollbar from 'components/ScrollBar';
import axiosInstance from 'utils/axios';
import FormEditName from 'page-sections/operator/registration/formEditName';
import PrintBadge from 'utils/PrintBadge';
import AlreadyRegisteredConfirmation from 'page-sections/operator/registration/alreadyRegisteredConfirmation';
import useBoolean from 'hooks/useBoolean';

const HeadTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&:first-of-type': {
        paddingLeft: 24,
    },
    '&:last-of-type': {
        paddingRight: 24,
    },
}));
const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Registration = () => {
    const columns = useMemo(() => columnShape, []);
    const [listData, setListData] = useState([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [title, setTitle] = useState();
    const [dataToEdit, setDataToEdit] = useState();
    const [dataConfirmation, setDataConfirmation] = useState();
    const [openModal, setOpenModal] = useState(false);
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [printingByKey, setPrintingByKey] = useState({});
    const [confirmationPrinting, setConfirmationPrinting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const openSnackBar = useBoolean();
    const theme = useTheme();
    const printingKeysRef = useRef(new Set());
    const requestSeqRef = useRef(0);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        state,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data: listData,
            manualSortBy: true,
            disableSortRemove: true,
            initialState: {
                sortBy: [{ id: 'registrationDate', desc: true }],
            },
        },
        useGlobalFilter,
        useSortBy,
        useRowSelect,
    ); // handle change pagination

    const sortById = state.sortBy?.[0]?.id;
    const sortByDesc = state.sortBy?.[0]?.desc;
    const sortParams = useMemo(() => {
        if (!sortById) {
            return { sortBy: undefined, sortOrder: undefined };
        }

        const sortOrder = sortByDesc ? 'DESC' : 'ASC';

        if (sortById === 'nickName') {
            return { sortBy: 'name', sortOrder };
        }
        if (sortById === 'email') {
            return { sortBy: 'email', sortOrder };
        }
        if (sortById === 'registrationDate') {
            return { sortBy: 'registrationDate', sortOrder };
        }

        return { sortBy: undefined, sortOrder: undefined };
    }, [sortById, sortByDesc]);

    const handleChange = (_, currentPageNo) => {
        setCurrentPage(currentPageNo);
    }; // handle change for tab list
    const [searchValue, setSearchValue] = useState(state.globalFilter);
    const handleSearch = useAsyncDebounce(
        (value) => setGlobalFilter(value || undefined),
        200,
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchValue, sortById, sortByDesc]);

    const getData = useCallback(async () => {
        const requestSeq = ++requestSeqRef.current;
        setIsLoading(true);

        try {
            const { data } = await axiosInstance.get(`/api/prv/re-registrations`, {
                params: {
                    page: currentPage,
                    limit: 10,
                    term: searchValue,
                    ...(sortParams.sortBy ? sortParams : {}),
                },
            });

            if (requestSeq !== requestSeqRef.current) return;

            const items = Array.isArray(data) ? data : data?.data ?? [];
            const meta = Array.isArray(data) ? null : data?.meta ?? null;
            if (meta?.totalPages) {
                setTotalPages(meta.totalPages);
            } else {
                setTotalPages(1);
            }
            setTotalMatches(meta?.total ?? items.length);
            setListData(items);
        } catch (err) {
            console.log(err);
        } finally {
            if (requestSeq === requestSeqRef.current) {
                setIsLoading(false);
            }
        }
    }, [currentPage, searchValue, sortParams]);
    useEffect(() => {
        getData();
    }, [getData]);

    const onEnter = async () => {
        const trimmedTerm = (searchValue || '').trim();
        if (!trimmedTerm) {
            setSnackbarMessage('Please enter QR code, email, or name');
            openSnackBar.onTrue();
            return;
        }

        if (totalMatches > 1) {
            setSnackbarMessage(
                'More than 1 participant found. Please refine your search.',
            );
            openSnackBar.onTrue();
            return;
        }

        const qrCodeToRegister =
            totalMatches === 1 ? listData?.[0]?.qrCode : trimmedTerm;
        if (!qrCodeToRegister) {
            setSnackbarMessage('QR code not found for selected participant');
            openSnackBar.onTrue();
            return;
        }

        await axiosInstance
            .patch(
                `/api/prv/re-registrations/registrations/${qrCodeToRegister}`,
            )
            .then(({ data }) => {
                setTitle(
                    data.message === 'Already registered'
                        ? 'Already Re-Register'
                        : 'Re-Register Successfully',
                );
                getData();
                handleSearch(searchValue);
                setDataConfirmation(data.data);
                setOpenConfirmation(true);
            })
            .catch((err) => {
                console.log(err);
                setSnackbarMessage(
                    err?.response?.data?.message || 'Re-Register failed',
                );
                openSnackBar.onTrue();
            });
    };

    const handleChangeName = (data) => {
        setDataToEdit(data);
        setOpenModal(true);
    };

    const getPrintKey = (data) => data?.id || data?.qrCode || data?.invoiceNumber;

    const handlePrint = async (data) => {
        const key = getPrintKey(data);
        if (!key) return;
        if (printingKeysRef.current.has(key)) return;

        printingKeysRef.current.add(key);
        setPrintingByKey((prev) => ({ ...prev, [key]: true }));

        try {
            const tasks = [PrintBadge(data)];
            if (!data?.registrationDate && data?.qrCode) {
                tasks.push(updateRegistrationDate(data.qrCode));
            }
            await Promise.allSettled(tasks);
        } catch (err) {
            console.log(err);
            setSnackbarMessage(
                err?.response?.data?.message || 'Failed to print badge',
            );
            openSnackBar.onTrue();
        } finally {
            printingKeysRef.current.delete(key);
            setPrintingByKey((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const updateRegistrationDate = async (qrCode) => {
        await axiosInstance
            .patch(`/api/prv/re-registrations/registrations/${qrCode}`)
            .then(() => getData());
    };

    const handleUserDataUpdate = async () => {
        await getData();
        await handleSearch(searchValue);
    };

    const onConfirm = async () => {
        if (confirmationPrinting) return;

        if (title === 'Re-Register Successfully') {
            const data = {
                nickName: dataConfirmation?.nickName,
                qrCode: dataConfirmation?.qrCode,
            };
            setConfirmationPrinting(true);
            try {
                await PrintBadge(data);
            } finally {
                setConfirmationPrinting(false);
                setOpenConfirmation(false);
            }
            return;
        }
        setOpenConfirmation(false);
    };

    const handleInfo = async (data) => {
        await axiosInstance
            .get(`/api/prv/re-registrations/${data.id}`)
            .then(({ data }) => {
                setTitle('Participant Information');
                setDataConfirmation(data);
                setOpenConfirmation(true);
            })
            .catch((err) => console.log(err));
    };

    return (
        <Box pt={2} pb={4}>
            <Snackbar
                open={openSnackBar.value}
                autoHideDuration={4000}
                onClose={openSnackBar.onFalse}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={openSnackBar.onFalse}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <FormEditName
                onSubmissionSuccess={handleUserDataUpdate}
                data={dataToEdit}
                open={openModal}
                onClose={() => setOpenModal(false)}
            />
            <AlreadyRegisteredConfirmation
                title={title}
                data={dataConfirmation}
                open={openConfirmation}
                onClose={() => setOpenConfirmation(false)}
                onConfirm={onConfirm}
                loading={confirmationPrinting}
            />
            <Typography variant="h3" component="h1" paragraph>
                Re-Registration
            </Typography>
            <Card
                sx={{
                    padding: '2rem 1rem',
                    textAlign: 'center',
                }}
            >
                <SearchArea
                    value={searchValue}
                    onChange={handleSearch}
                    setValue={setSearchValue}
                    onEnter={onEnter}
                />
                {isLoading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress />
                    </Box>
                )}
                <Box sx={{ position: 'relative' }}>
                    <Scrollbar autoHide={true}>
                        <Table
                            {...getTableProps()}
                            sx={{
                                minWidth: 900,
                            }}
                        >
                            <TableHead
                                sx={{
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {headerGroups.map((headerGroup, index) => (
                                    <TableRow
                                        key={index}
                                        {...headerGroup.getHeaderGroupProps()}
                                    >
                                        {headerGroup.headers.map(
                                            (column, index) => (
                                                <HeadTableCell
                                                    key={index}
                                                    {...column.getHeaderProps(
                                                        column.getSortByToggleProps(),
                                                    )}
                                                    sx={{
                                                        paddingY: 0,
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        borderBottom: 0,
                                                        width: column.width,
                                                        color: 'text.secondary',
                                                        minWidth: column.minWidth,
                                                        maxWidth: column.maxWidth,
                                                        '&:last-child': {
                                                            textAlign: 'center',
                                                        },
                                                    }}
                                                >
                                                    {column.render('Header')}
                                                </HeadTableCell>
                                            ),
                                        )}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody {...getTableBodyProps()}>
                                {rows.map((row, index) => {
                                    prepareRow(row);
                                    const isPrintingRow = Boolean(
                                        printingByKey[
                                            row.original?.id ||
                                                row.original?.qrCode ||
                                                row.original?.invoiceNumber
                                        ],
                                    );
                                    return (
                                        <TableRow
                                            {...row.getRowProps()}
                                            key={index}
                                        >
                                            {row.cells.map((cell, index) => {
                                                return (
                                                    <BodyTableCell
                                                        key={index}
                                                        {...cell.getCellProps()}
                                                    >
                                                        {cell.render('Cell', {
                                                            handlePrint,
                                                            handleChangeName,
                                                            handleInfo,
                                                            isPrinting: isPrintingRow,
                                                        })}
                                                    </BodyTableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Scrollbar>
                    {isLoading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255,255,255,0.6)',
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Box>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        count={totalPages}
                        page={currentPage}
                        disabled={isLoading}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default Registration;
